import { type FC, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { TFunction } from 'i18next';
import LongCard from 'page-sections/dashboards/CRM/LongCard';
import { leaveBalancesApi, LeaveBalances } from 'api';

interface Props {
  employeeId?: number;
  seasonId?: number;
  year?: number;
  t: TFunction;
}

const fmt = (h: number | null | undefined) => (h == null ? '-' : `${Math.round(h * 10) / 10}h`);

/**
 * Ferie / permessi balances (maturato → goduto → residuo) for the year.
 * Read-only; renders nothing if the employee has no entitlement configured,
 * so existing tenants that don't use accrual see no change.
 */
const EmployeeLeaveBalances: FC<Props> = ({ employeeId, seasonId, year, t }) => {
  const [balances, setBalances] = useState<LeaveBalances | null>(null);

  useEffect(() => {
    let active = true;
    if (!employeeId) return;
    leaveBalancesApi
      .getBalances(employeeId, { seasonId, year })
      .then((res) => { if (active) setBalances(res.data); })
      .catch(() => { if (active) setBalances(null); });
    return () => { active = false; };
  }, [employeeId, seasonId, year]);

  if (!balances) return null;
  const hasFerie = balances.ferie.entitlementHours != null;
  const hasPermessi = balances.permessi.entitlementHours != null;
  if (!hasFerie && !hasPermessi) return null;

  const ferieList = [
    { id: 1, amount: fmt(balances.ferie.maturedHours), title: t('leaveBalances.matured'), color: '#5479F7' },
    { id: 2, amount: fmt(balances.ferie.usedHours), title: t('leaveBalances.used'), color: '#E5402A' },
    { id: 3, amount: fmt(balances.ferie.residualHours), title: t('leaveBalances.residual'), color: '#13C552' },
  ];
  const permessiList = [
    { id: 1, amount: fmt(balances.permessi.maturedHours), title: t('leaveBalances.matured'), color: '#5479F7' },
    { id: 2, amount: fmt(balances.permessi.usedHours), title: t('leaveBalances.used'), color: '#E5402A' },
    { id: 3, amount: fmt(balances.permessi.residualHours), title: t('leaveBalances.residual'), color: '#13C552' },
  ];

  return (
    <>
      {hasFerie && (
        <Grid item md={12} xs={12}>
          <LongCard list={ferieList} longCardTitle={`${t('leaveBalances.ferieTitle')} ${balances.year}`} />
        </Grid>
      )}
      {hasPermessi && (
        <Grid item md={12} xs={12}>
          <LongCard list={permessiList} longCardTitle={`${t('leaveBalances.permessiTitle')} ${balances.year}`} />
        </Grid>
      )}
    </>
  );
};

export default EmployeeLeaveBalances;
